<script setup>
import {
  ref,
  reactive,
  defineEmits,
  onMounted,
  nextTick,
  getCurrentInstance,
} from 'vue'
import { focusNextField as focusNextFieldHelper } from '@/helpers'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/error-handler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanService from '@/services/LaporanService'
import ItemService from '@/services/ItemService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanService()
const itemService = new ItemService()

let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
let isLoading = ref(false)
// let isLoadingExportExcel = ref(false)
let form = reactive({
  date_from: new Date(),
  date_to: new Date(),
  item: null,
})
let submitted = ref(false)
let listItems = ref([])

const vm = getCurrentInstance().proxy

function focusNextField(refName, selectAll = false) {
  focusNextFieldHelper(vm, refName, selectAll)
}

const laterDate = (value) => {
  if (!value || !form.date_from) return true

  return (
    dayjs(value).startOf('day').valueOf() >=
    dayjs(form.date_from).startOf('day').valueOf()
  )
}

const rules = {
  date_from: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
  date_to: {
    required: helpers.withMessage(
      'Tanggal "s/d" harus diisi lebih dari atau sama dengan tanggal "dari".',
      required
    ),
    laterDate,
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {
  await nextTick()
  setTimeout(() => {
    focusNextField('item')
  }, 100)
})

function searchItem(event) {
  isLoading.value = true
  itemService
    .getList(event.query, 0)
    .then((res) => {
      listItems.value = res.data
    })
    .catch((err) => {
      errorHandler(err, 'Data Item', toast)
    })
    .finally(() => (isLoading.value = false))
}

function selectItem() {
  form.item = form.selectedItem ? form.selectedItem.id : null

  nextTick(() => {
    setTimeout(() => {
      focusNextField('date_from')
    }, 50)
  })
}

function onExport(isFormValid) {
  isLoadingExport.value = true
  const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  const itemId = form.item === null ? 0 : form.item

  submitted.value = true

  if (!isFormValid) {
    isLoadingExport.value = false
    return
  }

  laporanService
    .SalesDetail(startDate, endDate, itemId)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-penjualan-detail.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Laporan Daftar Penjualan', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  submitted.value = false

  form.date_from = new Date()
  form.date_to = new Date()
  form.supplier = null
  form.selectedItem = []
  form.item = null

  nextTick(() => {
    setTimeout(() => {
      focusNextField('item')
    }, 50)
  })
}
</script>

<template>
  <fx-report-container
    title="Laporan Penjualan Detail"
    :loading="isLoadingExport"
    @toggle="onToggle"
    @export="onExport"
  >
    <template #content>
      <form @submit.prevent="onExport(!v$.$invalid)">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-4">
            <AutoComplete
              ref="item"
              v-model="form.selectedItem"
              class="w-full"
              input-class="w-full"
              placeholder="Search Item"
              :suggestions="listItems"
              field="name"
              @focus="searchItem({ query: '' })"
              @complete="searchItem"
              @item-select="selectItem"
            >
              <template #item="slotProps">
                <div>
                  <b>{{ slotProps.item.sku }}</b>
                </div>
                <div>{{ slotProps.item.name }}</div>
              </template>
            </AutoComplete>
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              ref="date_from"
              v-model="v$.date_from.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_from.$invalid && submitted,
              }"
              placeholder="Tanggal (dari)"
              class="w-full"
              @date-select="focusNextField('date_to')"
            />
            <small
              v-if="
                (v$.date_from.$invalid && submitted) ||
                v$.date_from.$pending.$response
              "
              class="p-error"
              >{{ v$.date_from.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              ref="date_to"
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
              @date-select="focusNextField('btnCetak')"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-4">
            <Button
              :loading="isLoadingExport"
              type="submit"
              icon="pi pi-file-pdf"
              class="ml-2"
              label="Cetak"
            />
            <!-- <Button
              :loading="isLoadingExportExcel"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2"
              @click="onExportExcel(!v$.$invalid)"
            /> -->
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
